<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-6xl mx-auto space-y-8">
      
      <!-- Header -->
      <header class="flex justify-between items-center bg-zinc-900/50 backdrop-blur-md p-6 rounded-3xl shadow-sm border border-zinc-800/50 relative overflow-hidden">
        <div class="relative z-10 w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <router-link :to="{ name: 'music_analytics' }" class="inline-flex items-center gap-2 text-zinc-400 hover:text-emerald-400 transition-colors mb-2 text-sm font-medium">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
              Retour aux statistiques
            </router-link>
            <h1 class="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-400">Tous les Genres</h1>
            <p class="text-zinc-400 mt-1">{{ filteredGenres.length }} genres trouvés</p>
          </div>
          
          <div class="w-full md:w-auto relative">
            <input 
              v-model="searchQuery" 
              type="text" 
              placeholder="Rechercher un genre..." 
              class="w-full md:w-80 bg-zinc-800/80 border border-zinc-700/50 rounded-xl py-2.5 pl-10 pr-4 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
            >
            <svg class="w-5 h-5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
          </div>
        </div>
        
        <!-- Decorative blob -->
        <div class="absolute -right-10 -top-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl"></div>
      </header>

      <div v-if="loading" class="text-center py-20">
        <div class="w-16 h-16 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-zinc-500 animate-pulse">Chargement de vos genres...</p>
      </div>

      <div v-else-if="error" class="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl text-center">
        <p class="text-red-400">{{ error }}</p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        
        <router-link 
          v-for="(genre, index) in filteredGenres" 
          :key="genre.id"
          :to="{ name: 'music_genre_detail', params: { id: genre.id } }"
          class="group flex flex-col justify-center gap-2 p-5 rounded-2xl bg-zinc-900/40 backdrop-blur-sm border border-zinc-800/50 hover:bg-zinc-800/50 transition-all duration-300 relative overflow-hidden block cursor-pointer hover:scale-[1.02]"
        >
          <div class="flex justify-between items-start z-10">
            <h3 class="font-bold text-lg text-zinc-200 capitalize group-hover:text-emerald-400 transition-colors leading-tight line-clamp-2 pr-2">{{ genre.name }}</h3>
            <span class="text-xs font-mono text-emerald-500/80 bg-emerald-500/10 px-2 py-1 rounded-md">{{ genre.score }}</span>
          </div>

          <!-- Progress Bar Background -->
          <div class="absolute bottom-0 left-0 h-1 bg-emerald-500/20 transition-all duration-1000 ease-out z-0 group-hover:bg-emerald-500/40" :style="{ width: `${(genre.score / maxGenreCount) * 100}%` }"></div>
        </router-link>

        <div v-if="filteredGenres.length === 0" class="col-span-full p-8 text-center text-zinc-500">
          Aucun genre trouvé correspondant à votre recherche.
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useToast } from '../../composables/useToast'

const { error: showError } = useToast()
const loading = ref(true)
const error = ref('')
const searchQuery = ref('')

interface Genre {
  id: number
  name: string
  score: number
}

const allGenres = ref<Genre[]>([])

const maxGenreCount = computed(() => {
  if (allGenres.value.length === 0) return 1
  return Math.max(...allGenres.value.map(g => g.score))
})

const filteredGenres = computed(() => {
  if (!searchQuery.value) return allGenres.value
  const query = searchQuery.value.toLowerCase()
  return allGenres.value.filter(g => g.name.toLowerCase().includes(query))
})

const fetchGenres = async () => {
  try {
    const token = localStorage.getItem('apiToken')
    const response = await fetch('/api/music/analytics/genres', {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    if (!response.ok) throw new Error('Erreur réseau')
    
    allGenres.value = await response.json()
  } catch (e) {
    console.error(e)
    error.value = "Impossible de charger la liste des genres."
    showError(error.value)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchGenres()
})
</script>
